/**
 * Add Fine Discount — two-section modal:
 *   1. Fine + Rider details (read-only summary)
 *   2. Discount details — amount + reason dropdown; reason drives which
 *      fields render (impounding dates / authority / description).
 *
 * The discount is applied only to the FINE amount — admin fees and VAT
 * are preserved. Backend endpoint: POST /api/fines/{id}/discount
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const REASONS = [
  { code: 'smart_impounding', label: 'Discount due to acceptance of smart impounding of the Motorcycle' },
  { code: 'authority_offer',  label: 'Discount due to offers from Authorities' },
  { code: 'disputed_reversed', label: 'Fines disputed and reversed' },
  { code: 'other', label: 'Other reasons' },
];
const AUTHORITIES = ['Dubai Police', 'Sharjah Police', 'Ajman Police', 'RAK Police', 'Abu Dhabi Police'];

export default function AddFineDiscountDialog({ open, onOpenChange, fine, onSaved }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('smart_impounding');
  const [dateOfDiscount, setDateOfDiscount] = useState(new Date().toISOString().slice(0, 10));
  const [impoundStart, setImpoundStart] = useState('');
  const [impoundEnd, setImpoundEnd] = useState('');
  const [impoundAuthority, setImpoundAuthority] = useState('');
  const [discountFromAuthority, setDiscountFromAuthority] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!fine) return null;

  const impoundDays = (() => {
    if (!impoundStart || !impoundEnd) return null;
    const s = new Date(impoundStart), e = new Date(impoundEnd);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return null;
    return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
  })();

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('Enter a discount amount > 0'); return; }
    const fineAmt = Number(fine.amount || fine.fine_amount || 0);
    if (Number(amount) > fineAmt) { toast.error(`Discount cannot exceed fine amount (AED ${fineAmt}).`); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('amount', String(amount));
      fd.append('reason', reason);
      fd.append('date_of_discount', dateOfDiscount);
      if (reason === 'smart_impounding') {
        if (impoundStart) fd.append('impound_start_date', impoundStart);
        if (impoundEnd)   fd.append('impound_end_date', impoundEnd);
        if (impoundAuthority) fd.append('impound_authority', impoundAuthority);
      }
      if (reason === 'authority_offer' || reason === 'disputed_reversed') {
        if (discountFromAuthority) fd.append('discount_from_authority', discountFromAuthority);
      }
      if (reason === 'other') {
        if (description) fd.append('description', description);
      }
      if (file) fd.append('proof_file', file);
      await axios.post(`${API}/fines/${fine.id}/discount`, fd, {
        headers: getAuth().headers,
        timeout: 120000,
      });
      toast.success(`Discount of AED ${amount} recorded`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save discount');
    } finally { setSubmitting(false); }
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between text-xs py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value ?? '—'}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-fine-discount-dialog">
        <DialogHeader>
          <DialogTitle>Add Discount</DialogTitle>
          <DialogDescription>Discount is applied only to the fine amount. Admin fees and VAT stay unchanged.</DialogDescription>
        </DialogHeader>

        {/* Section 1 — read-only fine + rider info */}
        <div className="rounded border bg-slate-50 p-3 space-y-1" data-testid="discount-fine-info">
          <h4 className="text-xs font-semibold uppercase text-slate-600 mb-2">Fine &amp; Rider Information</h4>
          <Row label="Fine Ref" value={fine.fine_number || fine.reference_number} />
          <Row label="Fine Date" value={fine.fine_date || fine.date} />
          <Row label="Source" value={fine.fine_source || fine.source} />
          <Row label="Description" value={fine.description || fine.violation || fine.fine_reason} />
          <Row label="Fine Amount" value={`AED ${Number(fine.amount || fine.fine_amount || 0).toLocaleString()}`} />
          <Row label="Admin Fees" value={`AED ${Number(fine.admin_fees || fine.admin_fee || 0).toLocaleString()}`} />
          <Row label="Payment Status" value={fine.payment_status || fine.authority_payment_status || '—'} />
          <Row label="Bike Plate" value={fine.bike_plate || fine.plate_number} />
          <Row label="Lessee" value={fine.lessee_name} />
          <Row label="Rider (Fine By)" value={fine.fine_by_rider_name || fine.current_rider_name || fine.rider_name} />
        </div>

        {/* Section 2 — discount form */}
        <div className="space-y-3 mt-2">
          <h4 className="text-xs font-semibold uppercase text-slate-600">Discount Details</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Discount Amount (AED)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="discount-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Date of Discount</Label>
              <Input type="date" value={dateOfDiscount} onChange={(e) => setDateOfDiscount(e.target.value)} data-testid="discount-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="discount-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {reason === 'smart_impounding' && (
            <div className="rounded border p-3 space-y-2 bg-emerald-50/40">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date of start of impounding</Label>
                  <Input type="date" value={impoundStart} onChange={(e) => setImpoundStart(e.target.value)} data-testid="impound-start-input" />
                </div>
                <div>
                  <Label className="text-xs">Date of completion of impounding</Label>
                  <Input type="date" value={impoundEnd} onChange={(e) => setImpoundEnd(e.target.value)} data-testid="impound-end-input" />
                </div>
              </div>
              {impoundDays !== null && <p className="text-xs text-emerald-700">Impounding period: <strong>{impoundDays} day{impoundDays === 1 ? '' : 's'}</strong> (auto-calculated)</p>}
              <div>
                <Label className="text-xs">Authority of impounding</Label>
                <Select value={impoundAuthority} onValueChange={setImpoundAuthority}>
                  <SelectTrigger data-testid="impound-authority-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{AUTHORITIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {(reason === 'authority_offer' || reason === 'disputed_reversed') && (
            <div>
              <Label className="text-xs">{reason === 'authority_offer' ? 'Discount from — Name of Authority' : 'Dispute with — Name of Authority'}</Label>
              <Input value={discountFromAuthority} onChange={(e) => setDiscountFromAuthority(e.target.value)} data-testid="discount-authority-input" />
            </div>
          )}
          {reason === 'other' && (
            <div>
              <Label className="text-xs">Reason for discount</Label>
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="discount-description-input" />
            </div>
          )}

          <div>
            <Label className="text-xs">Upload discounted Invoice from Authority</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="discount-file-input" />
            {file && <p className="text-[10px] text-green-700 mt-1">{file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !amount} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-discount-btn">
            {submitting ? 'Saving…' : 'Save Discount'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
